import { c, classy, m } from '@onfido/castor';
import React, { useEffect, useMemo, useRef, useState, } from 'react';
import { useForwardedRef, withRef } from '../../../utils';
import { Popover } from '../../popover/popover';
import { NativeSelect } from '../native';
import { CustomSelectProvider } from './useCustomSelect';
export const CustomSelect = withRef(function CustomSelect({ align = 'start', borderless, children, className, defaultValue, name: initialName, open: isOpen, position = 'bottom', onBlur, onClick, onKeyUp, onOpenChange, value, ...restProps }, ref) {
    const selectRef = useForwardedRef(ref);
    const options = useRef(new Map());
    // initialize with empty array to ensure re-render even with nullish value
    const [currentValue, _setCurrentValue] = useState([]);
    const setCurrentValue = (value) => 
    // default to first option if value is not in options
    _setCurrentValue(options.current.has(value) ? value : options.current.keys().next().value);
    // set initial value only after options have been initialized
    useEffect(() => setCurrentValue(value !== null && value !== void 0 ? value : defaultValue), []);
    // set value every time it changes and it's not nullish
    useEffect(() => {
        if (value != null)
            setCurrentValue(value);
    }, [value]);
    const name = useMemo(() => initialName || `castor-select-${++id}`, [initialName]);
    const open = () => onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(true);
    const close = () => {
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(false);
        focus(selectRef.current);
    };
    const selectedOption = options.current.get(currentValue);
    return (React.createElement(CustomSelectProvider, { value: {
            name,
            value: currentValue,
            initialize(option, optionValue) {
                options.current.set(optionValue, option);
            },
            select(option, optionValue) {
                // if there are repeated keys, make the selected one take priority
                options.current.set(optionValue, option);
                setCurrentValue(optionValue);
                close();
                // propagate onChange manually because <select> won't naturally when
                // its value is changed programatically by React, and on next tick
                // because React needs to update its value first
                setTimeout(() => {
                    var _a;
                    return (_a = selectRef.current) === null || _a === void 0 ? void 0 : _a.dispatchEvent(new Event('change', { bubbles: true }));
                });
            },
        } },
        React.createElement(NativeSelect, { ...restProps, ref: selectRef, className: classy(m('absolute'), className), name: name, onBlur: (event) => {
                // if the Popover is open, focus is still inside Select
                // we don't want Field/Form validation to trigger
                if (isOpen)
                    event.stopPropagation();
                onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
            }, onClick: (event) => {
                isOpen ? close() : open();
                onClick === null || onClick === void 0 ? void 0 : onClick(event);
            }, onKeyUp: (event) => {
                if (openSelectKeys.has(event.key))
                    open();
                onKeyUp === null || onKeyUp === void 0 ? void 0 : onKeyUp(event);
            } }, !currentValue || React.createElement("option", { hidden: true, value: currentValue })),
        React.createElement("output", { className: classy(c('select-output')) },
            selectedOption,
            "\u00A0 "),
        isOpen && (React.createElement(Popover, { align: align, className: classy(c('select-dropdown'), m({ borderless })), overlay: true, position: position, target: selectRef, onClose: close, onKeyUp: (event) => {
                if (closeSelectKeys.has(event.key))
                    close();
            }, onKeyDown: (event) => {
                // close if focus moves outside
                if (event.key === 'Tab') {
                    setTimeout(close);
                    event.preventDefault();
                }
            }, onRender: (element) => {
                var _a;
                // focus :checked option if :enabled, otherwise first :enabled
                return focus((_a = element === null || element === void 0 ? void 0 : element.querySelector(':checked:enabled')) !== null && _a !== void 0 ? _a : element === null || element === void 0 ? void 0 : element.querySelector('input:enabled'));
            }, 
            // stop bubbling so that Field validation isn't affected
            onBlur: stopPropagation, onChange: stopPropagation, onInvalid: stopPropagation }, children)),
        !options.current.size && React.createElement("div", { hidden: true }, children)));
});
const closeSelectKeys = new Set(['Escape']);
const openSelectKeys = new Set([' ', 'ArrowDown', 'ArrowUp']);
const focus = (element) => element === null || element === void 0 ? void 0 : element.focus({ preventScroll: true });
const stopPropagation = (event) => event.stopPropagation();
let id = 0;
//# sourceMappingURL=custom-select.js.map